import { useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { History, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { PRIMARY, fmt } from "./shared";

export const VERDICTS = {
  strengthening:     { label: "Strengthening", bg: "#ECFDF5", text: "#065F46", Icon: TrendingUp },
  stalled:           { label: "Stalled",       bg: "#F4F4F5", text: "#52525B", Icon: Minus },
  weakening:         { label: "Weakening",     bg: "#FEF2F2", text: "#DC2626", Icon: TrendingDown },
  insufficient_data: { label: "Too little data", bg: "#FAFAFA", text: "#A1A1AA", Icon: Minus },
};

export const METRICS = {
  queries:     { label: "Ranking queries", hint: "Distinct queries with impressions — breadth, moves first" },
  top10_share: { label: "Top-10 share",    hint: "% of impressions from queries ranking 1–10", unit: "%" },
  position:    { label: "Avg position",    hint: "Impression-weighted; lower is better", reversed: true },
  clicks:      { label: "Clicks",          hint: "Moves last" },
  impressions: { label: "Impressions",     hint: "" },
};

const signed = (n, unit = "%") => (n == null ? "—" : `${n > 0 ? "+" : ""}${n}${unit}`);

export function verdictDetail(a) {
  if (!a || a.verdict === "insufficient_data") return "Not enough search data in the last 8 weeks to judge.";
  return `Last 4 weeks vs the 4 before: ranking queries ${signed(a.breadth_change)}, top-10 share ${signed(a.top10_change_pp, "pp")}.`;
}

export function VerdictChip({ authority }) {
  const v = VERDICTS[authority?.verdict] || VERDICTS.insufficient_data;
  return (
    <span
      title={verdictDetail(authority)}
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded-full whitespace-nowrap"
      style={{ background: v.bg, color: v.text }}
    >
      <v.Icon size={10} /> {v.label}
    </span>
  );
}

// Tiny inline trend of one metric — no axes; the row's verdict chip carries the meaning.
export function Sparkline({ series, metric = "queries", color = PRIMARY, width = 72, height = 22 }) {
  const vals = (series || []).map(w => w[metric] ?? 0);
  if (vals.length < 2 || vals.every(v => v === 0)) {
    return <span className="inline-block text-[9px] text-zinc-300" style={{ width }}>no data</span>;
  }
  const max = Math.max(...vals), min = Math.min(...vals);
  const span = max - min || 1;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * width},${height - 2 - ((v - min) / span) * (height - 4)}`);
  return (
    <svg width={width} height={height} className="flex-shrink-0" aria-hidden="true">
      <polyline points={pts.join(" ")} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export function BackfillCard({ state, onStart, weeksStored }) {
  const running = state?.status === "running";
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3.5 mb-4 bg-white border border-zinc-100 rounded-xl">
      <History size={16} className="text-zinc-400" />
      <div className="flex-1 min-w-[220px]">
        <p className="text-xs font-bold text-zinc-800">Track whether each topic is gaining authority</p>
        <p className="text-[11px] text-zinc-400 mt-0.5">
          {running
            ? state.message
            : state?.status === "failed"
              ? <span className="text-red-500">{state.message}</span>
              : `Pulls ~15 months of weekly Search Console history (${weeksStored} week${weeksStored === 1 ? "" : "s"} stored so far). New weeks are added automatically every Tuesday.`}
        </p>
        {running && (
          <div className="h-1 mt-2 bg-zinc-100 rounded-full overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${state.progress || 2}%`, background: PRIMARY }} />
          </div>
        )}
      </div>
      <button
        onClick={onStart}
        disabled={running}
        className="px-3 py-1.5 text-[11px] font-bold text-white rounded-lg disabled:opacity-40"
        style={{ background: PRIMARY }}
      >
        {running ? "Building history…" : "Build history"}
      </button>
    </div>
  );
}

const fmtWeek = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

export function AuthorityChart({ authority, lines }) {
  const [metric, setMetric] = useState("queries");
  const m = METRICS[metric];
  const site = authority.site;
  const sv = VERDICTS[site?.verdict] || VERDICTS.insufficient_data;

  const data = authority.weeks.map((week, i) => {
    const row = { week };
    for (const l of lines) row[l.id] = authority.clusters[l.id]?.series[i]?.[metric] ?? null;
    return row;
  });

  return (
    <div className="bg-white border border-zinc-100 rounded-xl mb-4">
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-4">
        <div>
          <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400">Site topical authority</p>
          <p className="text-sm font-bold mt-1 flex items-center gap-1.5" style={{ color: sv.text === "#52525B" ? "#18181b" : sv.text }}>
            <sv.Icon size={15} /> {sv.label}
          </p>
          <p className="text-[11px] text-zinc-400 mt-0.5">{verdictDetail(site)}</p>
        </div>
        <div className="flex gap-0.5 bg-zinc-100 rounded-lg p-0.5">
          {Object.entries(METRICS).map(([key, { label, hint }]) => (
            <button
              key={key}
              onClick={() => setMetric(key)}
              title={hint}
              className="px-2.5 py-1 text-[10px] font-bold rounded-md whitespace-nowrap"
              style={metric === key
                ? { background: "#fff", color: "#18181b", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }
                : { color: "#71717a" }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-56 px-2 pt-3">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#F4F4F5" vertical={false} />
            <XAxis dataKey="week" tickFormatter={fmtWeek} tick={{ fontSize: 10, fill: "#A1A1AA" }} axisLine={false} tickLine={false} minTickGap={24} />
            <YAxis
              reversed={!!m.reversed}
              tick={{ fontSize: 10, fill: "#A1A1AA" }}
              axisLine={false}
              tickLine={false}
              width={40}
              tickFormatter={v => fmt(v) + (m.unit || "")}
            />
            <Tooltip
              labelFormatter={w => `Week of ${fmtWeek(w)}`}
              formatter={(v, id) => [v == null ? "—" : fmt(v, 1) + (m.unit || ""), lines.find(l => l.id === id)?.name || id]}
              contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E4E4E7" }}
            />
            {lines.map(l => (
              <Line key={l.id} dataKey={l.id} name={l.name} stroke={l.color} strokeWidth={2} dot={false} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 px-5 pb-4 pt-1">
        {lines.map(l => (
          <span key={l.id} className="inline-flex items-center gap-1.5 text-[11px] text-zinc-600">
            <span className="w-2.5 h-0.5 rounded" style={{ background: l.color }} />
            {l.name}
          </span>
        ))}
      </div>
    </div>
  );
}
